import React, { useState } from 'react';
import { ManagedImage } from '../ManagedImage';
import { FitControls } from './FitControls';
import { setSiteImage, updateSiteImageFit } from '../../lib/portfolio';

// Editor för en enskild "plats" (hero, tjänsteomslag, porträtt).
// `image` = { url?, basePath?, filename?, alt, fit, focusX, focusY }
export const SlotEditor = ({ slot, label, help, aspect = '4/3', image, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [fitVal, setFitVal] = useState({ fit: image.fit, focusX: image.focusX, focusY: image.focusY });

  const isDb = Boolean(image.url); // finns i databasen (utbytbar passning)
  const imgProps = { url: image.url, basePath: image.basePath, filename: image.filename, alt: label };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    setStatus('');
    try {
      await setSiteImage(slot, file, fitVal);
      setStatus('✓ Bilden är sparad');
      onChanged();
    } catch (err) {
      setStatus('Något gick fel: ' + err.message);
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const saveFit = async () => {
    setBusy(true);
    setStatus('');
    try {
      await updateSiteImageFit(slot, fitVal);
      setStatus('✓ Passningen är sparad');
      onChanged();
    } catch (err) {
      setStatus('Något gick fel: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-cv-base border border-cv-border flex flex-col gap-4" style={{ borderRadius: 'var(--cv-radius)', padding: 24 }}>
      <div className="flex flex-col gap-1">
        <h3 className="m-0 font-serif font-medium text-[20px] text-cv-ink">{label}</h3>
        {help && <p className="m-0 text-sm text-cv-muted leading-[1.6]">{help}</p>}
      </div>

      <div className="flex flex-wrap gap-6 items-start">
        <div className="overflow-hidden bg-cv-panel flex-shrink-0" style={{ width: 260, aspectRatio: aspect, borderRadius: 'var(--cv-radius)' }}>
          <ManagedImage {...imgProps} fit={fitVal.fit} focusX={fitVal.focusX} focusY={fitVal.focusY} className="w-full h-full" />
        </div>

        <div className="flex flex-col gap-4 flex-1 min-w-[260px]">
          <label className={`w-fit inline-flex items-center gap-2 bg-cv-ink text-cv-base px-6 py-3 text-[13px] tracking-[0.14em] uppercase rounded-cv cursor-pointer hover:bg-cv-ink2 transition-colors ${busy ? 'opacity-60 pointer-events-none' : ''}`}>
            {busy ? 'Arbetar…' : 'Byt bild'}
            <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
          </label>

          {isDb ? (
            <>
              <FitControls imgProps={imgProps} value={fitVal} onChange={setFitVal} />
              <button
                type="button"
                onClick={saveFit}
                disabled={busy}
                className="w-fit bg-transparent border border-cv-edge text-cv-ink px-6 py-2.5 text-[13px] tracking-[0.14em] uppercase rounded-cv hover:bg-cv-line transition-colors cursor-pointer disabled:opacity-60"
              >
                Spara passning
              </button>
            </>
          ) : (
            <p className="m-0 text-sm text-cv-muted leading-[1.6]">
              Detta är en grundbild. Ladda upp din egen (eller kör seed-scriptet) så kan du även justera hur den passas in.
            </p>
          )}

          {status && <p className="m-0 text-sm text-cv-ink2">{status}</p>}
        </div>
      </div>
    </div>
  );
};
